<template lang="pug">
.column.relative_position_materials_search
  .equipment-tech-map.history
    .tech-map-card
      .tabs-block
        table#components-table.tech-map-tab-table.components-table.stores
          thead
            tr
              th(v-for="title in tableColumns", :key="title.name", @click="changeSortParams(title)")
                span(:class="getHeaderClass(title.sortable)") {{ title.label }}
                  q-icon(:name="iconName()", v-if="title.sortable")
          tbody(v-infinite-scroll="loadMore", infinite-scroll-distance="20")
            template(v-if="tableData.length")
              tr(v-for="item in tableData", :key="item.id")
                td(
                  v-for="title in tableColumns",
                  :key="`${title.name} ${item.id}`",
                  :class="getTabCeilClasses(title.name)",
                  @click="openIssueCard(item, title)"
                ) {{ getCeilValue(title, item) }}
              tr(v-if="loadingMore")
                td.loading-spinner
                  q-spinner(color="primary", size="3em")
            template(v-else)
              tr(v-if="loadingTable")
                td.loading-spinner
                  q-spinner(color="primary", size="3em")
              tr(v-else)
                td.text-center(colspan="6") {{ notifies.data_not_found }}
  grid-modals(
    ref="issueModal",
    :parentData="{ grid: 'issues', grid_key: 'data', path: issuesPath, facility_id: facilityId, transition_key: 'issue', row: {} }"
  )
  grid-modals(
    ref="pprIssueModal",
    :parentData="{ grid: 'ppr_issues', grid_key: 'data', path: pprIssuesPath, facility_id: facilityId, row: {} }"
  )
</template>

<script setup>
import { ref, computed } from "vue";
import { navbarLocales } from "@/services/useLocales";
import GridModals from "@/components/shared/grid/gridModals";
import { parseISO, format } from "date-fns";

const props = defineProps({
  /**
   * Дата для таблицы. Пример: [
      {
        "id": 1906,
        "user": "Jarrell карф Boyle",
        "material_title": "материал 18",
        "store_id": 62,
        "store_title": "сумма",
        "count": 31,
        "cost": "1000.0",
        "issue_id": 151019,
        "issue_type": "issue",
        "created_at": "2024-12-11T12:25:01.547Z",
        "store_sender_title": null,
        "store_motion_title": null
      },
    ]
   */
  data: { type: Array, default: () => [] },
  /**
   * Данные для создания заголовков таблицы. Пример: [
      {
          "name": "user",
          "label": "Пользователь",
          "field": "user",
          "classes": "consumption-user-column",
          "column_style": {
              "width": "15%"
          },
          "row_style": {},
          "sortable": true
      },
    ]
   */
  columns: { type: Array, default: () => [] },
  /**
   * Флаг для запроса следующей страницы пагниации данных таблицы. Пример: true
   */
  hasNextPage: { type: Boolean, default: () => false },
  /**
   * Флаг отвечающий за подгрузку данных таблицы при пагинации. Пример: false
   */
  loadingMore: { type: Boolean, default: () => false },
  /**
   * Флаг отвечающий за загрузку данных таблицы при сортировке. Пример: false
   */
  loadingTable: { type: Boolean, default: () => false },
});

const emit = defineEmits(["loadMore", "reloadData"]);

const pprIssuesPath = "/api/v3/ppr/issues";
const issuesPath = "/api/v3/dynamic/issues";

const activeSort = ref("id");
const activeDirection = ref("down");
const facilityId = ref(null);

const issueModal = ref(null);
const pprIssueModal = ref(null);

const tableData = computed(() => props.data);
const tableColumns = computed(() => props.columns);
const loadingMore = computed(() => props.loadingMore);
const loadingTable = computed(() => props.loadingTable);

const openIssueCard = (issue, title) => {
  if (title.name === "issue_id") {
    // Присваивание нужно чтобы передавать facility_id в grid_modals
    // Он может иметь разное значение, у разных заявок свой facility_id
    facilityId.value = issue.facility_id;
    const issueId = { id: issue.issue_id };

    if (issue.issue_type === "issue") {
      issueModal.value.openShow(issueId);
    } else {
      pprIssueModal.value.openShow(issueId);
    }
  }
};

const iconName = () => {
  return activeDirection.value === "up" ? "expand_less" : "expand_more";
};

const changeSortParams = title => {
  if (title.sortable) {
    const val = title.name;

    if (val === activeSort.value) {
      if (activeDirection.value === "down") {
        activeDirection.value = "up";
      } else {
        activeDirection.value = "down";
      }
    } else {
      activeSort.value = val;
      activeDirection.value = "down";
    }

    reloadTableWithSort();
  }
};

const loadMore = () => {
  if (props.hasNextPage && !loadingMore.value) {
    emit("loadMore");
  }
};

const reloadTableWithSort = () => {
  emit("reloadData", {
    sort: activeSort.value,
    sort_direction: activeDirection.value,
  });
};

const getTabCeilClasses = key => {
  if (key === "issue_id") {
    return "consumptions-history__issue_cell";
  } else {
    return "";
  }
};

const getCeilValue = (column, item) => {
  if (column.name === "issue_id") {
    return `№ ${item.issue_id}`;
  } else if (column.type === "date" && item[column.name]) {
    return format(parseISO(item[column.name]), "dd.MM.yyyy");
  } else {
    // Возвращает значение ячейки таблицы
    return item[column.name];
  }
};

const getHeaderClass = sortable => {
  const classes = ["column-header-title"];

  if (sortable) classes.push("pointer");

  return classes.join(" ");
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/styles/show/ppr/equipment/main";
.relative_position_materials_search {
  .tabs-block {
    // 77vh остаточная высота для таблицы за исключением другого выделенного места
    // 100px это высота табов с отступами
    max-height: calc(77vh - 100px) !important;
  }

  .equipment-tech-map.history {
    margin-top: 15px;

    .loading-spinner {
      min-height: 60px !important;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .column-header-title.pointer {
      cursor: pointer;
    }
  }
}

.consumptions-history__issue_cell {
  cursor: pointer;
  color: #5b7aff !important;
  text-decoration: underline;
}
</style>
